import { ENDPOINTS } from "./endpoints"

export type TranscriptSessionStatus =
  | "uploading"
  | "transcribing"
  | "scoring"
  | "complete"
  | "error"
  | string

export type TranscriptSessionSource =
  | "web_mic"
  | "upload"
  | "mobile_app"
  | "manual"
  | string

export type TranscriptSessionModality = "in_person" | "telehealth"

export interface TranscriptSessionSummary {
  id: string
  title: string | null
  createdAt: string
  status: TranscriptSessionStatus
  durationSec: number | null
  overallScore: number | null
  errorMessage: string | null
  source: TranscriptSessionSource | null
  clientId: string | null
  practitionerId: string | null
  modality: TranscriptSessionModality | null
}

export interface TranscriptSegment {
  startSec: number
  role: string | null
  speaker: string | null
  text: string
}

export interface TranscriptDocument {
  segments: TranscriptSegment[]
  rawText: string
}

export interface TranscriptDimension {
  name: string
  score: number
  rationale: string | null
  isNa: boolean
}

export interface TranscriptFeedbackItem {
  text: string
  excerpt: string | null
  suggestion: string | null
  segmentRefs: number[]
}

export interface TranscriptHighlight {
  segmentRef: number
  sentiment: string
}

export interface TranscriptScoreResult {
  overallScore: number
  confidence: string | null
  dimensions: TranscriptDimension[]
  strengths: TranscriptFeedbackItem[]
  areasForAttention: TranscriptFeedbackItem[]
  highlights: TranscriptHighlight[]
}

export interface TranscriptSessionDetail {
  session: TranscriptSessionSummary
  transcript: TranscriptDocument | null
  scoreResult: TranscriptScoreResult | null
}

interface TranscriptSessionFilters {
  clientId: string
  practitionerId: string
}

interface TranscriptUploadRequest extends TranscriptSessionFilters {
  file: File
}

interface TranscriptManualSessionRequest extends TranscriptSessionFilters {
  modality?: TranscriptSessionModality
  title?: string
}

interface TranscriptAudioRequest extends TranscriptSessionFilters {
  audioBlob: Blob
  filename: string
  startedAt: string
  endedAt: string
  modality: TranscriptSessionModality
  title?: string
}

/**
 * Formats the default title assigned to a microphone transcript session.
 */
export function formatDefaultTranscriptTitle(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
  startedAt: Date
): string {
  const initials = [getInitial(firstName), getInitial(lastName)]
    .filter(Boolean)
    .join("")

  const clientLabel = initials || "Client"
  const dateLabel = startedAt.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
  const timeLabel = startedAt.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  })

  return `${clientLabel} - ${dateLabel} ${timeLabel}`
}

class TranscriptApiError extends Error {
  status: number

  constructor(message: string, status: number) {
    super(message)
    this.status = status
  }
}

/**
 * Returns transcript sessions scoped to a client and practitioner.
 */
export async function fetchTranscriptSessions(
  jwt: string,
  filters: TranscriptSessionFilters
): Promise<TranscriptSessionSummary[]> {
  const searchParams = new URLSearchParams({
    client_id: filters.clientId,
    practitioner_id: filters.practitionerId,
  })
  const response = await transcriptFetch(
    `${ENDPOINTS.TRANSCRIPT_SESSIONS}?${searchParams.toString()}`,
    jwt
  )
  const body = await parseJson(response)

  if (!Array.isArray(body)) {
    throw new TranscriptApiError("Unexpected transcript session response.", 500)
  }

  return body
    .map(parseSessionSummary)
    .filter(
      (session) =>
        (!session.clientId || session.clientId === filters.clientId) &&
        (!session.practitionerId ||
          session.practitionerId === filters.practitionerId)
    )
}

/**
 * Returns transcript session detail for the selected session.
 */
export async function fetchTranscriptSessionDetail(
  jwt: string,
  sessionId: string
): Promise<TranscriptSessionDetail> {
  const response = await transcriptFetch(
    `${ENDPOINTS.TRANSCRIPT_SESSIONS}/${encodeURIComponent(sessionId)}`,
    jwt
  )
  const body = await parseJson(response)

  if (!isRecord(body) || !isRecord(body.session)) {
    throw new TranscriptApiError("Unexpected transcript session detail.", 500)
  }

  return {
    session: parseSessionSummary(body.session),
    transcript: parseTranscriptDocument(body.transcript),
    scoreResult: parseScoreResult(body.score_result),
  }
}

/**
 * Creates a transcript session from a microphone recording.
 */
export async function createTranscriptAudioSession(
  jwt: string,
  request: TranscriptAudioRequest
): Promise<{ sessionId: string }> {
  const formData = new FormData()
  formData.append("audio", request.audioBlob, request.filename)
  formData.append("client_id", request.clientId)
  formData.append("practitioner_id", request.practitionerId)
  formData.append("started_at", request.startedAt)
  formData.append("ended_at", request.endedAt)
  formData.append("modality", request.modality)
  if (request.title) {
    formData.append("title", request.title)
  }

  const response = await transcriptFetch(ENDPOINTS.TRANSCRIPT_RECORD, jwt, {
    method: "POST",
    body: formData,
  })
  const body = await parseJson(response)

  return parseCreatedSession(body)
}

/**
 * Creates a transcript session from an uploaded transcript document.
 */
export async function createTranscriptUploadSession(
  jwt: string,
  request: TranscriptUploadRequest
): Promise<{ sessionId: string }> {
  const formData = new FormData()
  formData.append("file", request.file)
  formData.append("client_id", request.clientId)
  formData.append("practitioner_id", request.practitionerId)

  const response = await transcriptFetch(ENDPOINTS.TRANSCRIPT_UPLOAD, jwt, {
    method: "POST",
    body: formData,
  })
  const body = await parseJson(response)

  return parseCreatedSession(body)
}

/**
 * Attaches a transcript document to an existing empty session shell.
 */
export async function attachTranscriptUploadToSession(
  jwt: string,
  sessionId: string,
  file: File
): Promise<{ sessionId: string }> {
  const formData = new FormData()
  formData.append("file", file)

  const response = await transcriptFetch(
    `${ENDPOINTS.TRANSCRIPT_SESSIONS}/${encodeURIComponent(sessionId)}/upload`,
    jwt,
    {
      method: "POST",
      body: formData,
    }
  )
  const body = await parseJson(response)
  return parseCreatedSession(body)
}

/**
 * Attaches a microphone recording to an existing empty session shell.
 */
export async function attachTranscriptAudioToSession(
  jwt: string,
  sessionId: string,
  request: Omit<
    TranscriptAudioRequest,
    "clientId" | "practitionerId" | "title"
  > & {
    title?: string
  }
): Promise<{ sessionId: string }> {
  const formData = new FormData()
  formData.append("audio", request.audioBlob, request.filename)
  formData.append("started_at", request.startedAt)
  formData.append("ended_at", request.endedAt)
  formData.append("modality", request.modality)
  if (request.title) {
    formData.append("title", request.title)
  }

  const response = await transcriptFetch(
    `${ENDPOINTS.TRANSCRIPT_SESSIONS}/${encodeURIComponent(sessionId)}/record`,
    jwt,
    {
      method: "POST",
      body: formData,
    }
  )
  const body = await parseJson(response)
  return parseCreatedSession(body)
}

/**
 * Creates a completed session without audio or transcript (plan + note only).
 */
export async function createManualTranscriptSession(
  jwt: string,
  request: TranscriptManualSessionRequest
): Promise<{ sessionId: string }> {
  const response = await transcriptFetch(
    ENDPOINTS.TRANSCRIPT_MANUAL_SESSION,
    jwt,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        client_id: request.clientId,
        modality: request.modality ?? "in_person",
        title: request.title,
      }),
    }
  )
  const body = await parseJson(response)
  return parseCreatedSession(body)
}

/**
 * Deletes a transcript session.
 */
export async function deleteTranscriptSession(
  jwt: string,
  sessionId: string
): Promise<void> {
  await transcriptFetch(
    `${ENDPOINTS.TRANSCRIPT_SESSIONS}/${encodeURIComponent(sessionId)}`,
    jwt,
    {
      method: "DELETE",
    }
  )
}

/**
 * Updates display metadata for a transcript session (title and/or clinical start).
 */
export async function updateTranscriptSession(
  jwt: string,
  sessionId: string,
  updates: { title?: string; startedAt?: string }
): Promise<TranscriptSessionSummary> {
  const body: Record<string, string> = {}
  if (updates.title !== undefined) {
    body.title = updates.title
  }
  if (updates.startedAt !== undefined) {
    body.started_at = updates.startedAt
  }
  const response = await transcriptFetch(
    `${ENDPOINTS.TRANSCRIPT_SESSIONS}/${encodeURIComponent(sessionId)}`,
    jwt,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    }
  )
  const parsed = await parseJson(response)
  return parseSessionSummary(parsed)
}

/**
 * Updates the display title for a transcript session.
 * @deprecated Prefer `updateTranscriptSession`.
 */
export async function renameTranscriptSession(
  jwt: string,
  sessionId: string,
  title: string
): Promise<TranscriptSessionSummary> {
  return updateTranscriptSession(jwt, sessionId, { title })
}

export type CarePlanStatus =
  | "draft"
  | "approved"
  | "signed"
  | "composing"
  | "composed"
  | "composition_failed"
  | string

export type CarePlanItemDecision =
  | "approved"
  | "rejected"
  | "edited"
  | "signed"
  | "draft"
  | string

export interface CarePlanContingency {
  id: string
  templateId: string | null
  if: string | null
  then: string | null
  approved: boolean
  requiresApproval: boolean
  sourceQuote: string | null
}

export interface CarePlanItem {
  id: string
  title: string | null
  goal: string | null
  rationale: string | null
  status: CarePlanItemDecision | null
  interventionId: string | null
  /** Pipeline / clinician natural-language timing hint. */
  scheduleHint: string | null
  parameters: Record<string, unknown>
  personalization: Record<string, unknown>
  contingencies: CarePlanContingency[]
}

export interface CarePlanResponse {
  sessionId: string
  status: CarePlanStatus | null
  plan: {
    schemaVersion: string | null
    knowledgeVersion: string | null
    items: CarePlanItem[]
    raw: Record<string, unknown>
  } | null
  approvedItems: Record<string, CarePlanItemDecision>
  isFallback: boolean
  guardrails: unknown
  signedAt: string | null
  compositionError: string | null
  compositionNote: string | null
}

function actionPlanUrl(sessionId: string, suffix = ""): string {
  return `${ENDPOINTS.TRANSCRIPT_SESSIONS}/${encodeURIComponent(
    sessionId
  )}/action-plan${suffix}`
}

/**
 * Returns the persisted care plan for a completed transcript session.
 */
export async function fetchCarePlan(
  jwt: string,
  sessionId: string
): Promise<CarePlanResponse> {
  const response = await transcriptFetch(actionPlanUrl(sessionId), jwt)
  return parseCarePlanResponse(await parseJson(response), sessionId)
}

/**
 * Generates a draft care plan from the session transcript (Foundry pipeline).
 */
export async function generateCarePlan(
  jwt: string,
  sessionId: string
): Promise<CarePlanResponse> {
  const response = await transcriptFetch(
    actionPlanUrl(sessionId, "/generate"),
    jwt,
    {
      method: "POST",
    }
  )
  return parseCarePlanResponse(await parseJson(response), sessionId)
}

/**
 * Creates an empty editable action plan when no transcript is available.
 */
export async function initializeManualCarePlan(
  jwt: string,
  sessionId: string
): Promise<CarePlanResponse> {
  const response = await transcriptFetch(
    actionPlanUrl(sessionId, "/initialize"),
    jwt,
    { method: "POST" }
  )
  return parseCarePlanResponse(await parseJson(response), sessionId)
}

/**
 * Patches plan body and/or per-item decisions while the plan is still editable.
 */
export async function patchCarePlan(
  jwt: string,
  sessionId: string,
  body: {
    plan?: Record<string, unknown>
    approvedItems?: Record<string, CarePlanItemDecision>
  }
): Promise<CarePlanResponse> {
  const response = await transcriptFetch(actionPlanUrl(sessionId), jwt, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      plan: body.plan,
      approved_items: body.approvedItems,
    }),
  })
  return parseCarePlanResponse(await parseJson(response), sessionId)
}

/**
 * Marks a plan item as approved for signing.
 */
export async function acceptCarePlanItem(
  jwt: string,
  sessionId: string,
  itemId: string
): Promise<CarePlanResponse> {
  const response = await transcriptFetch(
    actionPlanUrl(sessionId, `/items/${encodeURIComponent(itemId)}/accept`),
    jwt,
    { method: "POST" }
  )
  return parseCarePlanResponse(await parseJson(response), sessionId)
}

/**
 * Marks a plan item as rejected (excluded on sign).
 */
export async function rejectCarePlanItem(
  jwt: string,
  sessionId: string,
  itemId: string
): Promise<CarePlanResponse> {
  const response = await transcriptFetch(
    actionPlanUrl(sessionId, `/items/${encodeURIComponent(itemId)}/reject`),
    jwt,
    { method: "POST" }
  )
  return parseCarePlanResponse(await parseJson(response), sessionId)
}

/**
 * Edits mutable fields on a single plan item.
 */
export async function patchCarePlanItem(
  jwt: string,
  sessionId: string,
  itemId: string,
  body: {
    title?: string
    goal?: string
    parameters?: Record<string, unknown>
    personalization?: Record<string, unknown>
    contingencies?: CarePlanContingency[]
    scheduleHint?: string | null
  }
): Promise<CarePlanResponse> {
  const payload: Record<string, unknown> = {}
  if (body.title !== undefined) {
    payload.title = body.title
  }
  if (body.goal !== undefined) {
    payload.goal = body.goal
  }
  if (body.parameters !== undefined) {
    payload.parameters = body.parameters
  }
  if (body.personalization !== undefined) {
    payload.personalization = body.personalization
  }
  if (body.contingencies !== undefined) {
    payload.contingencies = body.contingencies.map(serializeCarePlanContingency)
  }
  if (body.scheduleHint !== undefined) {
    payload.schedule_hint = body.scheduleHint
  }
  const response = await transcriptFetch(
    actionPlanUrl(sessionId, `/items/${encodeURIComponent(itemId)}`),
    jwt,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  )
  return parseCarePlanResponse(await parseJson(response), sessionId)
}

/**
 * Signs approved items and starts async composition (Phase 2 stubs composition).
 */
export async function signCarePlan(
  jwt: string,
  sessionId: string
): Promise<CarePlanResponse> {
  const response = await transcriptFetch(
    actionPlanUrl(sessionId, "/sign"),
    jwt,
    {
      method: "POST",
    }
  )
  return parseCarePlanResponse(await parseJson(response), sessionId)
}

function parseCarePlanResponse(
  body: unknown,
  fallbackSessionId: string
): CarePlanResponse {
  if (!isRecord(body)) {
    throw new TranscriptApiError("Unexpected care plan response.", 500)
  }

  const planRaw = isRecord(body.plan) ? body.plan : null
  const items = Array.isArray(planRaw?.items)
    ? planRaw.items
        .map(parseCarePlanItem)
        .filter((item): item is CarePlanItem => Boolean(item))
    : []

  const approvedItems: Record<string, CarePlanItemDecision> = {}
  if (isRecord(body.approved_items)) {
    for (const [key, value] of Object.entries(body.approved_items)) {
      if (typeof value === "string") {
        approvedItems[key] = value
      }
    }
  }

  return {
    sessionId:
      typeof body.session_id === "string" ? body.session_id : fallbackSessionId,
    status: typeof body.status === "string" ? body.status : null,
    plan: planRaw
      ? {
          schemaVersion:
            typeof planRaw.schema_version === "string"
              ? planRaw.schema_version
              : null,
          knowledgeVersion:
            typeof planRaw.knowledge_version === "string"
              ? planRaw.knowledge_version
              : null,
          items,
          raw: planRaw,
        }
      : null,
    approvedItems,
    isFallback: Boolean(body.is_fallback),
    guardrails: body.guardrails ?? null,
    signedAt: typeof body.signed_at === "string" ? body.signed_at : null,
    compositionError:
      typeof body.composition_error === "string"
        ? body.composition_error
        : null,
    compositionNote:
      typeof body.composition_note === "string" ? body.composition_note : null,
  }
}

function parseCarePlanItem(body: unknown): CarePlanItem | null {
  if (!isRecord(body)) {
    return null
  }
  const id =
    typeof body.id === "string"
      ? body.id
      : typeof body.plan_item_id === "string"
      ? body.plan_item_id
      : null
  if (!id) {
    return null
  }

  return {
    id,
    title: typeof body.title === "string" ? body.title : null,
    goal: typeof body.goal === "string" ? body.goal : null,
    rationale: typeof body.rationale === "string" ? body.rationale : null,
    status: typeof body.status === "string" ? body.status : null,
    interventionId:
      typeof body.intervention_id === "string" ? body.intervention_id : null,
    scheduleHint:
      typeof body.schedule_hint === "string"
        ? body.schedule_hint
        : typeof body.scheduleHint === "string"
        ? body.scheduleHint
        : null,
    parameters: isRecord(body.parameters) ? body.parameters : {},
    personalization: isRecord(body.personalization) ? body.personalization : {},
    contingencies: Array.isArray(body.contingencies)
      ? body.contingencies
          .map(parseCarePlanContingency)
          .filter((item): item is CarePlanContingency => Boolean(item))
      : [],
  }
}

function parseCarePlanContingency(body: unknown): CarePlanContingency | null {
  if (!isRecord(body) || typeof body.id !== "string") {
    return null
  }
  return {
    id: body.id,
    templateId:
      typeof body.template_id === "string"
        ? body.template_id
        : typeof body.templateId === "string"
        ? body.templateId
        : null,
    if: typeof body.if === "string" ? body.if : null,
    then: typeof body.then === "string" ? body.then : null,
    approved: Boolean(body.approved),
    requiresApproval:
      typeof body.requires_approval === "boolean"
        ? body.requires_approval
        : typeof body.requiresApproval === "boolean"
        ? body.requiresApproval
        : true,
    sourceQuote:
      typeof body.source_quote === "string"
        ? body.source_quote
        : typeof body.sourceQuote === "string"
        ? body.sourceQuote
        : null,
  }
}

function serializeCarePlanContingency(
  contingency: CarePlanContingency
): Record<string, unknown> {
  return {
    id: contingency.id,
    template_id: contingency.templateId,
    if: contingency.if,
    then: contingency.then,
    approved: contingency.approved,
    requires_approval: contingency.requiresApproval,
    source_quote: contingency.sourceQuote,
  }
}

async function transcriptFetch(
  url: string,
  jwt: string,
  init?: RequestInit
): Promise<Response> {
  const headers = new Headers(init?.headers ?? {})
  headers.set("Authorization", `Bearer ${jwt}`)

  const response = await fetch(url, {
    ...init,
    headers,
  })

  if (!response.ok) {
    const detail = await readErrorDetail(response)
    throw new TranscriptApiError(detail, response.status)
  }

  return response
}

async function parseJson(response: Response): Promise<unknown> {
  try {
    return await response.json()
  } catch {
    throw new TranscriptApiError(
      "Transcript service returned invalid JSON.",
      500
    )
  }
}

async function readErrorDetail(response: Response): Promise<string> {
  try {
    const body = await response.json()
    if (isRecord(body)) {
      if (typeof body.detail === "string" && body.detail.length > 0) {
        return body.detail
      }
      if (Array.isArray(body.detail)) {
        const messages = body.detail
          .map((item) =>
            isRecord(item) && typeof item.msg === "string" ? item.msg : null
          )
          .filter((item): item is string => Boolean(item))
        if (messages.length > 0) {
          return messages.join("; ")
        }
      }
    }
  } catch {
    return "Transcript service request failed."
  }

  return "Transcript service request failed."
}

function parseCreatedSession(body: unknown): { sessionId: string } {
  if (!isRecord(body) || typeof body.session_id !== "string") {
    throw new TranscriptApiError(
      "Transcript service did not return a session id.",
      500
    )
  }

  return { sessionId: body.session_id }
}

/** Safe copy for clinicians when processing fails. */
export const CLINICIAN_TRANSCRIPT_ERROR =
  "This session couldn’t be processed. Please try again or contact support if it continues."

/**
 * Never surface Azure/SDK dumps or stack traces in the clinician UI.
 * Short, intentional product messages pass through unchanged.
 */
export function toClinicianTranscriptError(
  message: string | null | undefined,
  fallback: string = CLINICIAN_TRANSCRIPT_ERROR
): string | null {
  if (message == null) {
    return null
  }
  const trimmed = message.trim()
  if (!trimmed) {
    return null
  }
  if (isTechnicalTranscriptError(trimmed)) {
    return fallback
  }
  return trimmed
}

function isTechnicalTranscriptError(message: string): boolean {
  if (message.length > 180) {
    return true
  }
  return /DefaultAzureCredential|EnvironmentCredential|WorkloadIdentity|ManagedIdentity|SharedTokenCache|VisualStudioCodeCredential|AzureCliCredential|AzureDeveloperCliCredential|ClientSecretCredential|InteractiveBrowserCredential|Traceback \(most recent call last\)|aka\.ms\/azsdk|File "[/\\]|Exception:|stack trace|IMDS endpoint|token_file_path/i.test(
    message
  )
}

function parseSessionSummary(body: unknown): TranscriptSessionSummary {
  if (!isRecord(body) || typeof body.id !== "string") {
    throw new TranscriptApiError("Transcript session is missing an id.", 500)
  }

  return {
    id: body.id,
    title: typeof body.title === "string" ? body.title : null,
    createdAt:
      typeof body.started_at === "string"
        ? body.started_at
        : typeof body.created_at === "string"
        ? body.created_at
        : new Date().toISOString(),
    status: typeof body.status === "string" ? body.status : "error",
    durationSec:
      typeof body.duration_sec === "number" ? body.duration_sec : null,
    overallScore:
      typeof body.overall_score === "number" ? body.overall_score : null,
    errorMessage:
      typeof body.error_message === "string"
        ? toClinicianTranscriptError(body.error_message)
        : null,
    source: typeof body.source === "string" ? body.source : null,
    clientId: typeof body.client_id === "string" ? body.client_id : null,
    practitionerId:
      typeof body.practitioner_id === "string" ? body.practitioner_id : null,
    modality: parseSessionModality(body.modality),
  }
}

function parseSessionModality(
  value: unknown
): TranscriptSessionModality | null {
  return value === "in_person" || value === "telehealth" ? value : null
}

function parseTranscriptDocument(body: unknown): TranscriptDocument | null {
  if (!isRecord(body) || !Array.isArray(body.segments)) {
    return null
  }

  return {
    segments: body.segments.map(parseTranscriptSegment).filter(Boolean),
    rawText: typeof body.raw_text === "string" ? body.raw_text : "",
  }
}

function parseTranscriptSegment(body: unknown): TranscriptSegment {
  const data = isRecord(body) ? body : {}

  return {
    startSec: typeof data.start_sec === "number" ? data.start_sec : 0,
    role: typeof data.role === "string" ? data.role : null,
    speaker: typeof data.speaker === "string" ? data.speaker : null,
    text: typeof data.text === "string" ? data.text : "",
  }
}

function parseScoreResult(body: unknown): TranscriptScoreResult | null {
  if (!isRecord(body) || typeof body.overall_score !== "number") {
    return null
  }

  return {
    overallScore: body.overall_score,
    confidence: typeof body.confidence === "string" ? body.confidence : null,
    dimensions: Array.isArray(body.dimensions)
      ? body.dimensions.map(parseDimension)
      : [],
    strengths: Array.isArray(body.strengths)
      ? body.strengths.map(parseFeedbackItem)
      : [],
    areasForAttention: Array.isArray(body.areas_for_attention)
      ? body.areas_for_attention.map(parseFeedbackItem)
      : [],
    highlights: Array.isArray(body.highlights)
      ? body.highlights.map(parseHighlight)
      : [],
  }
}

function parseDimension(body: unknown): TranscriptDimension {
  const data = isRecord(body) ? body : {}

  return {
    name: typeof data.name === "string" ? data.name : "Untitled",
    score: typeof data.score === "number" ? data.score : 0,
    rationale: typeof data.rationale === "string" ? data.rationale : null,
    isNa: Boolean(data.is_na),
  }
}

function parseFeedbackItem(body: unknown): TranscriptFeedbackItem {
  const data = isRecord(body) ? body : {}

  return {
    text: typeof data.text === "string" ? data.text : "",
    excerpt: typeof data.excerpt === "string" ? data.excerpt : null,
    suggestion: typeof data.suggestion === "string" ? data.suggestion : null,
    segmentRefs: Array.isArray(data.segment_refs)
      ? data.segment_refs.filter(
          (value): value is number => typeof value === "number"
        )
      : [],
  }
}

function getInitial(name: string | null | undefined): string {
  return name?.trim().charAt(0).toUpperCase() ?? ""
}

function parseHighlight(body: unknown): TranscriptHighlight {
  const data = isRecord(body) ? body : {}

  return {
    segmentRef: typeof data.segment_ref === "number" ? data.segment_ref : 0,
    sentiment: typeof data.sentiment === "string" ? data.sentiment : "neutral",
  }
}

export type ProgressNoteStatus = "draft" | "signed"

export interface ProgressNoteResponse {
  id: string | null
  clientId: string | null
  practitionerId: string | null
  sessionId: string | null
  status: ProgressNoteStatus | null
  soapText: string
  birpText: string
  /** ISO timestamp from the API when present (used for Prepare source meta). */
  updatedAt: string | null
}

function progressNoteUrl(sessionId: string, suffix = ""): string {
  return `${ENDPOINTS.TRANSCRIPT_SESSIONS}/${encodeURIComponent(
    sessionId
  )}/progress-note${suffix}`
}

function clientProgressNotesUrl(
  clientId: string,
  noteId?: string,
  suffix = ""
): string {
  const base = `${ENDPOINTS.TRANSCRIPT_CLIENTS}/${encodeURIComponent(
    clientId
  )}/progress-notes`
  if (!noteId) {
    return `${base}${suffix}`
  }
  return `${base}/${encodeURIComponent(noteId)}${suffix}`
}

function parseProgressNoteResponse(
  body: unknown,
  fallbackSessionId?: string | null
): ProgressNoteResponse {
  if (!isRecord(body)) {
    throw new TranscriptApiError("Unexpected progress note response.", 500)
  }

  const status =
    body.status === "draft" || body.status === "signed" ? body.status : null

  return {
    id: typeof body.id === "string" ? body.id : null,
    clientId: typeof body.client_id === "string" ? body.client_id : null,
    practitionerId:
      typeof body.practitioner_id === "string" ? body.practitioner_id : null,
    sessionId:
      typeof body.session_id === "string"
        ? body.session_id
        : fallbackSessionId ?? null,
    status,
    soapText: typeof body.soap_text === "string" ? body.soap_text : "",
    birpText: typeof body.birp_text === "string" ? body.birp_text : "",
    updatedAt: typeof body.updated_at === "string" ? body.updated_at : null,
  }
}

/**
 * Returns the persisted progress note for a completed transcript session.
 */
export async function fetchProgressNote(
  jwt: string,
  sessionId: string
): Promise<ProgressNoteResponse> {
  const response = await transcriptFetch(progressNoteUrl(sessionId), jwt)
  return parseProgressNoteResponse(await parseJson(response), sessionId)
}

/**
 * Generates a SOAP or BIRP draft from the session transcript (Foundry).
 * Overwrites only the requested format; the other format is preserved.
 * Pass `clientContext` (Vira sensing/PROs) so the note matches Promptflow #58.
 */
export async function generateProgressNote(
  jwt: string,
  sessionId: string,
  noteFormat: "soap" | "birp" = "soap",
  clientContext?: Record<string, unknown> | null
): Promise<ProgressNoteResponse> {
  const response = await transcriptFetch(
    `${progressNoteUrl(
      sessionId,
      "/generate"
    )}?note_format=${encodeURIComponent(noteFormat)}`,
    jwt,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        client_context: clientContext ?? null,
      }),
    }
  )
  return parseProgressNoteResponse(await parseJson(response), sessionId)
}

/**
 * Saves draft SOAP and/or BIRP text. Omitted fields are left unchanged server-side.
 */
export async function saveProgressNoteDraft(
  jwt: string,
  sessionId: string,
  body: { soapText?: string; birpText?: string }
): Promise<ProgressNoteResponse> {
  const payload: Record<string, string> = {}
  if (body.soapText !== undefined) {
    payload.soap_text = body.soapText
  }
  if (body.birpText !== undefined) {
    payload.birp_text = body.birpText
  }

  const response = await transcriptFetch(progressNoteUrl(sessionId), jwt, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  })
  return parseProgressNoteResponse(await parseJson(response), sessionId)
}

/**
 * Finalizes both SOAP and BIRP texts (one-way).
 */
export async function approveProgressNote(
  jwt: string,
  sessionId: string,
  body: { soapText: string; birpText: string }
): Promise<ProgressNoteResponse> {
  const response = await transcriptFetch(
    progressNoteUrl(sessionId, "/approve"),
    jwt,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        soap_text: body.soapText,
        birp_text: body.birpText,
      }),
    }
  )
  return parseProgressNoteResponse(await parseJson(response), sessionId)
}

/**
 * Creates a client-scoped progress note (session optional).
 */
export async function createClientProgressNote(
  jwt: string,
  clientId: string,
  body: {
    soapText?: string
    birpText?: string
    sessionId?: string | null
  } = {}
): Promise<ProgressNoteResponse> {
  const payload: Record<string, string> = {}
  if (body.soapText !== undefined) {
    payload.soap_text = body.soapText
  }
  if (body.birpText !== undefined) {
    payload.birp_text = body.birpText
  }
  if (body.sessionId) {
    payload.session_id = body.sessionId
  }

  const response = await transcriptFetch(
    clientProgressNotesUrl(clientId),
    jwt,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  )
  return parseProgressNoteResponse(await parseJson(response))
}

/**
 * Lists progress notes for a client owned by the current practitioner.
 */
export async function listClientProgressNotes(
  jwt: string,
  clientId: string
): Promise<ProgressNoteResponse[]> {
  const response = await transcriptFetch(clientProgressNotesUrl(clientId), jwt)
  const body = await parseJson(response)
  if (!Array.isArray(body)) {
    throw new TranscriptApiError("Unexpected progress note list response.", 500)
  }
  return body.map((item) => parseProgressNoteResponse(item))
}

/**
 * Returns one client-scoped progress note.
 */
export async function fetchClientProgressNote(
  jwt: string,
  clientId: string,
  noteId: string
): Promise<ProgressNoteResponse> {
  const response = await transcriptFetch(
    clientProgressNotesUrl(clientId, noteId),
    jwt
  )
  return parseProgressNoteResponse(await parseJson(response))
}

/**
 * Saves a client-scoped progress note draft (and optionally links a session).
 */
export async function saveClientProgressNoteDraft(
  jwt: string,
  clientId: string,
  noteId: string,
  body: {
    soapText?: string
    birpText?: string
    sessionId?: string | null
  }
): Promise<ProgressNoteResponse> {
  const payload: Record<string, string | null> = {}
  if (body.soapText !== undefined) {
    payload.soap_text = body.soapText
  }
  if (body.birpText !== undefined) {
    payload.birp_text = body.birpText
  }
  if (body.sessionId !== undefined) {
    payload.session_id = body.sessionId
  }

  const response = await transcriptFetch(
    clientProgressNotesUrl(clientId, noteId),
    jwt,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  )
  return parseProgressNoteResponse(await parseJson(response))
}

/**
 * Finalizes a client-scoped progress note.
 */
export async function approveClientProgressNote(
  jwt: string,
  clientId: string,
  noteId: string,
  body: { soapText: string; birpText: string }
): Promise<ProgressNoteResponse> {
  const response = await transcriptFetch(
    clientProgressNotesUrl(clientId, noteId, "/approve"),
    jwt,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        soap_text: body.soapText,
        birp_text: body.birpText,
      }),
    }
  )
  return parseProgressNoteResponse(await parseJson(response))
}

function isRecord(value: unknown): value is Record<string, any> {
  return typeof value === "object" && value !== null
}
